import React, { useEffect, useRef } from 'react';
import CoffeeRain from '../components/CoffeeRain';
import image1 from '../aboutuspage_origin.png';
import image2 from '../aboutuspage_vancouver.png';
import image3 from '../aboutuspage_values.jpg';
import image4 from '../aboutuspage_origin.png';
import image5 from '../aboutuspage_influencer.png';

function AboutUs() {
  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ]; // Adjusted to 5 sections

  useEffect(() => {
    const observers = sectionRefs.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const section = entry.target;
              const h2 = section.querySelector('h2');
              const img = section.querySelector('img');
              if (h2) h2.style.transform = 'translateX(0)';
              if (img) img.style.transform = 'translateY(0)';
            }
          });
        },
        { threshold: 0.5 } // Trigger when 50% of the section is visible
      );
      if (ref.current) observer.observe(ref.current);
      return observer;
    });

    // Cleanup observers on unmount
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  useEffect(() => {
    // Define and inject CSS to hide scrollbars
    const styles = `
      @keyframes slide {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(0); }
      }
      @keyframes slideUp {
        0% { transform: translateY(50px); }
        100% { transform: translateY(0); }
      }
      /* Hide scrollbar for WebKit browsers (Chrome, Safari) */
      .body::-webkit-scrollbar {
        display: none;
      }
      /* Hide scrollbar for Firefox */
      .body {
        scrollbar-width: none;
      }
      /* Ensure sections clear floated elements */
      .section-clearfix::after {
        content: '';
        display: table;
        clear: both;
      }
      /* iOS Safari specific scrolling */
      .body {
        -webkit-overflow-scrolling: touch;
      }
    `;
    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);

    // Cleanup on unmount
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <div
      className="body"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: '#f9f9f9',
        position: 'relative',
      }}
    >
      <CoffeeRain />
      {/* Decorative Shapes (static) */}
      <div
        style={{
          position: 'absolute',
          top: '-50px',
          left: '-50px',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, #F48E28, transparent)',
          borderRadius: '50%',
          opacity: '0.5',
          pointerEvents: 'none', // Prevent interference with touch
        }}
      ></div>
      <div
        style={{
          position: 'absolute',
          bottom: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'linear-gradient(45deg, #A68368, transparent)',
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          opacity: '0.4',
          pointerEvents: 'none', // Prevent interference with touch
        }}
      ></div>

      <div
        style={{
          padding: '16px',
          flex: 1,
          overflowY: 'auto', // Emulate Blog's scrollable inner div
        }}
      >
        <h1
          style={{
            paddingTop: '140px',
            margin: '0 0 20px 0',
            color: '#A68368',
            textAlign: 'center',
            fontSize: '2.8em', // Increased from 2.5em
            borderBottom: '3px solid #F48E28',
            paddingBottom: '10px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          About Us
        </h1>

        {/* Origin Story Section */}
        <section
          ref={sectionRefs[0]}
          className="section-clearfix"
          style={{
            maxWidth: '800px',
            margin: '0 auto 40px auto',
            color: '#6C441A',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.2em', // Increased from 2em
              color: '#F48E28',
              marginBottom: '10px',
              transition: 'transform 1s',
              transform: 'translateX(-100%)',
            }}
          >
            The Coffee Revolution Kicks Off
          </h2>
          <p style={{ fontSize: '1.2em' }}>BeanBonus popped up just over a year ago in Vancouver when we noticed something funky: small coffee shops were missing out on the cool tools big chains like Starbucks use! So, we whipped up this awesome app to give the little guys a boost. Imagine us chilling in Mount Pleasant, sipping coffee, and dreaming up this wild loyalty revolution—now we’re live with our first cafes!</p>
          <p style={{ fontSize: '1.2em' }}>We’re just getting going, turning late-night coffee chats into something big. Hop on board from Gastown to Kitsilano!</p>
          <img
            src={image1}
            alt="Origin Illustration"
            style={{
              width: '100%',
              maxWidth: '400px',
              borderRadius: '10px',
              marginTop: '10px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 1s',
              transform: 'translateY(50px)',
            }}
          />
        </section>

        {/* Vancouver Connection Section */}
        <section
          ref={sectionRefs[1]}
          className="section-clearfix"
          style={{
            maxWidth: '800px',
            margin: '0 auto 40px auto',
            color: '#6C441A',
            backgroundColor: '#fff',
            borderLeft: '5px solid #A68368',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.2em', // Increased from 2em
              color: '#F48E28',
              marginBottom: '10px',
              transition: 'transform 1s',
              transform: 'translateX(-100%)',
            }}
          >
            Our Vancouver Roots
          </h2>
          <p style={{ fontSize: '1.2em' }}>BeanBonus calls Vancouver home—the ultimate coffee hangout where every spot has its own vibe! We’re shining a light on indie cafes such as Storm City and Coffee Villain, making them super easy to find with our app.</p>
          <p style={{ fontSize: '1.2em' }}>With Stanley Park’s green goodness and North Shore’s mountain views, we’re all about exploring local gems. Join our cafe crawls and scavenger hunts to discover—and help these small shops shine!</p>
          <img
            src={image2}
            alt="Vancouver Scene"
            style={{
              width: '100%',
              maxWidth: '400px',
              borderRadius: '10px',
              marginTop: '10px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 1s',
              transform: 'translateY(50px)',
              float: 'right',
            }}
          />
        </section>

        {/* Values Section */}
        <section
          ref={sectionRefs[2]}
          className="section-clearfix"
          style={{
            maxWidth: '800px',
            margin: '0 auto 40px auto',
            color: '#6C441A',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.2em', // Increased from 2em
              color: '#F48E28',
              marginBottom: '10px',
              transition: 'transform 1s',
              transform: 'translateX(-100%)',
            }}
          >
            Powering the Coffee Scene
          </h2>
          <p style={{ fontSize: '1.2em' }}>Support? We’re rooting for small cafes with tools like loyalty perks and push alerts! Community? We’re linking coffee fans with local spots through fun events like paint nights and comedy gigs. Innovation? Mobile ordering is coming soon to shake things up!</p>
          <p style={{ fontSize: '1.2em' }}>Transparency keeps it real—every feature lifts indie cafes. Let’s brew a stronger coffee crew together!</p>
          <img
            src={image3}
            alt="Values Illustration"
            style={{
              width: '100%',
              maxWidth: '400px',
              borderRadius: '10px',
              marginTop: '10px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 1s',
              transform: 'translateY(50px)',
            }}
          />
        </section>

        {/* Affiliate Program Section */}
        <section
          ref={sectionRefs[3]}
          className="section-clearfix"
          style={{
            maxWidth: '800px',
            margin: '0 auto 40px auto',
            color: '#6C441A',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.2em', // Increased from 2em
              color: '#F48E28',
              marginBottom: '10px',
              transition: 'transform 1s',
              transform: 'translateX(-100%)',
            }}
          >
            {"Affiliate Program: Spread The Buzz"}
          </h2>
          <p style={{ fontSize: '1.2em' }}>Love coffee and Instagram? Jump into our Affiliate & Microinfluencer Program! Snap pics of your fave Vancouver cafes, share the love, and score rewards for every new BeanBonus pal you bring. It’s a win for you and the coffee world!</p>
          <p style={{ fontSize: '1.2em' }}>Whether you’re a coffee buff or an Insta-whiz, email <a href="mailto:info@beanbonus.ca" style={{ color: '#F48E28', textDecoration: 'none', fontWeight: 'bold' }}>info@beanbonus.ca</a> or slide into our DM's to join <a href="https://instagram.com/beanbonus" style={{ color: '#F48E28', textDecoration: 'none', fontWeight: 'bold' }}>BeanBonus</a>. let’s make some waves together!</p>
          <img
            src={image5}
            alt="Affiliate Promotion"
            style={{
              width: '100%',
              maxWidth: '400px',
              borderRadius: '10px',
              marginTop: '10px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 1s',
              transform: 'translateY(50px)',
            }}
          />
        </section>

        {/* Contact Us Section */}
        <section
          ref={sectionRefs[4]}
          className="section-clearfix"
          style={{
            maxWidth: '800px',
            margin: '0 auto 40px auto',
            color: '#6C441A',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.2em', // Increased from 2em
              color: '#F48E28',
              marginBottom: '10px',
              transition: 'transform 1s',
              transform: 'translateX(-100%)',
            }}
          >
            Contact Us
          </h2>
          <p style={{ fontSize: '1.2em' }}>Got a cool cafe idea, a collab dream, or the coziest Vancouver spot to share? Shoot us a message at <a href="mailto:info@beanbonus.ca" style={{ color: '#F48E28', textDecoration: 'none', fontWeight: 'bold' }}>info@beanbonus.ca</a>! Check out our coffee fun on <a href="https://instagram.com/beanbonus" style={{ color: '#A68368', textDecoration: 'none', fontWeight: 'bold' }}>Instagram</a>.</p>
          <p style={{ fontSize: '1.2em' }}>We’re based in Vancouver, BC, just kicking off with our first cafes, and love your input.</p>
        </section>
      </div>

      {/* Footer Spacer */}
      <div style={{ flexGrow: 0, height: '100px' }}></div>
    </div>
  );
}

export default AboutUs;